import { useEffect, useState } from 'react';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import Table from '../Components/Tables';
import { formatDateUTC } from '../utils/formatDate';

import { useNavigate, useParams } from 'react-router-dom';
import Button from '../Components/Buttons';
import {
  ConnectionObjectApiProvider,
  useConnectionObjectApi,
} from '../Services/ConnectionObjectApiProvider';
import { getTablePaginationFromStorage } from '../utils/tablePaginationStorage';
import { formatTableTitle } from '../utils/formatTitle';

const connectionObjectTypeLabels = {
  LEAD: 'Lead',
  PROCESS: 'Process',
  FILE: 'File',
  BUYER: 'Buyer',
  CARD: 'Card',
  GRAPHQL_OBJECT: 'GraphQL Object',
  HTTP_OBJECT: 'HTTP Object',
};

function ConnectionObjectHub({ canEdit }) {
  const { getConnectionObjects, deleteConnectionObject } = useConnectionObjectApi();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connectionObjects, setConnectionObjects] = useState([]);
  const [totalConnectionObjects, setTotalConnectionObjects] = useState(0);
  const navigate = useNavigate();

  const tableTitle = 'Connection Objects';
  const { persistedPageSize, persistedSkip } = getTablePaginationFromStorage(
    formatTableTitle(tableTitle),
  );

  const columns = [
    { name: 'ID', selector: (row) => row.id, sortable: true },
    { name: 'Name', selector: (row) => row.name, sortable: true },
    {
      name: 'Type',
      selector: (row) => connectionObjectTypeLabels[row.type] || row.type,
      sortable: true,
    },
    { name: 'Status', selector: (row) => (row.is_active ? 'Active' : 'Inactive'), sortable: true },
    { name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: true },
    { name: 'Updated', selector: (row) => formatDateUTC(row.updated_at), sortable: true },
  ];

  const fetchConnectionObjects = async (skip, limit) => {
    try {
      setLoading(true);
      const response = await getConnectionObjects(skip, limit);
      setConnectionObjects(response.items);
      setTotalConnectionObjects(response.totalCount);
    } catch (error) {
      console.error('Error fetching connection objects:', error);
      setError('Failed to load connection objects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConnectionObjects(persistedSkip, persistedPageSize);
  }, []);

  const handleRowClicked = (row) => {
    navigate(`/organizations/${organizationId}/connection-objects/${row.id}`);
  };

  const handleDelete = async (selectedId) => {
    if (!selectedId) return;
    showConfirmation(
      `Are you sure you want to delete the selected Connection Object?`,
      async () => {
        try {
          setLoading(true);
          await deleteConnectionObject(selectedId);
          fetchConnectionObjects(persistedSkip, persistedPageSize);
          showAlert('success', 'Connection Object was successfully deleted!');
        } catch (error) {
          showAlert('fail', 'Failed to delete the connection object!');
        }
      },
    );
  };

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title={tableTitle}
                columns={columns}
                data={connectionObjects}
                selectableRowsHighlight
                selectableRows
                progressPending={loading}
                paginationPerPage={persistedPageSize}
                onRowClicked={handleRowClicked}
                onDelete={handleDelete}
                paginationTotalRows={totalConnectionObjects}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onChangePage={fetchConnectionObjects}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() =>
                    navigate(
                      `/organizations/${organizationId}/connection-objects/new-connection-object`,
                    )
                  }
                >
                  Create New Connection Object
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ConnectionObjectHubWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <ConnectionObjectApiProvider>
        <ConnectionObjectHub canEdit={canEdit} />
      </ConnectionObjectApiProvider>
    </ConfirmationProvider>
  );
}

export default ConnectionObjectHubWrapper;
